import React from "react";
//#region types
import { TranslationKeys } from "translations";
import { StyleEnums } from "models/enums";
import { CurrencyCode } from "models/enums/currency";
import { Amount } from "models/utilTypes/currencyType";
import { WintSuperkollAPIModel, WintUtilsWebApi } from "wint-api-client/dist/types";
//#endregion
//#region components
import { LoadingWithLogo, PageLayouts, RouteBreadCrumb, StaticInfoMessage, TableAsync } from "components/common";
import { Grid } from "@mui/material";
import { useNavigate } from "react-router-dom";
import useVatReportPermissions from "./usePermissions";
import VatReportsAvailabilityFailed from "./availabilityFailed";
import VatReportStatusTag from "./statusTag";
import VatReportDueDate from "./dueDate";
import { useVatReportService } from "serviceHooks";
//#endregion
import { formatVatPeriod } from "./vatReportModel";
import { LocalizationHelperInstance, UrlHelper } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

type Row = WintSuperkollAPIModel.VatReportSummary;

// The server caps a page at 12 rows because each row reads its box 49 from Wint Accounting.
const ROWS_PER_PAGE = 12;

const MISSING = "–";

/**
 * The company's VAT reports, newest period first, as a sub-page of Rådgivningsvyn. No FilterButton: a
 * company has about twelve reports a year.
 */
const VatReportsList: React.FC = () => {
  const navigate = useNavigate();
  const { access, retryAvailability } = useVatReportPermissions();
  const { listReports } = useVatReportService();

  const [loadFailed, setLoadFailed] = React.useState<boolean>(false);

  const filter = React.useCallback(
    (baseFilter: WintUtilsWebApi.Filter) =>
      listReports(baseFilter)
        .then((res) => {
          setLoadFailed(false);
          return res;
        })
        // TableAsync renders a failed fetch as an empty table, and "no reports" and "we could not
        // ask" must not look the same to the advisor.
        .catch((error) => {
          setLoadFailed(true);
          throw error;
        }),
    // `listReports` is fresh each render; TableAsync refetches whenever this function changes.
    []
  );

  const locale = LocalizationHelperInstance.intl.locale;
  const title = LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Advisor.VatReports);

  // DashboardLayout answers "no permission" for a false hasPermissions, so it must only see a real no:
  // not the check still on its way, nor a check that could not be answered.
  if (access === "checking") return <LoadingWithLogo />;
  if (access === "failed") return <VatReportsAvailabilityFailed title={title} onRetry={retryAvailability} />;

  return (
    <PageLayouts.DashboardLayout
      entityKey={0}
      hasPermissions={access === "granted"}
      title={title}
      // Without `current` the empty index child supplies the leaf and the crumb repeats itself.
      breadCrumb={<RouteBreadCrumb className={cssExports.dashBoardNavigation} current={title} />}
    >
      <Grid container item direction="column" rowGap={2}>
        {loadFailed && (
          <StaticInfoMessage displayType={StyleEnums.DisplayType.Danger} surface="background">
            {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Advisor.VatReportsLoadFailed)}
          </StaticInfoMessage>
        )}
        <TableAsync<Row>
          tableId="advisorVatReports"
          filter={filter}
          maxRowCount={ROWS_PER_PAGE}
          // The endpoint answers newest period first and ignores ordering, so a sortable header
          // would move nothing but its arrow.
          disableSort
          rowClickHandler={(row: Row) => row.id && navigate(UrlHelper.formatPath(`/AdvisoryView/VatReports/${row.id}`))}
          properties={{
            start: {
              description: TranslationKeys.Common.Period,
              noWrap: true,
              formatter: (r: Row) => formatVatPeriod(r.start, r.end, locale),
            },
            status: {
              description: TranslationKeys.Common.Status,
              noWrap: true,
              formatter: (r: Row) => <VatReportStatusTag status={r.status} filedByCustomer={r.filedByCustomer} />,
            },
            dueDate: {
              description: TranslationKeys.Common.Due_Date,
              noWrap: true,
              formatter: (r: Row) =>
                r.dueDate ? <VatReportDueDate dueDate={r.dueDate} isOverdue={r.isOverdue} /> : MISSING,
            },
            amountToPay: {
              description: TranslationKeys.Advisor.VatReportAmountToPay,
              noWrap: true,
              alignment: "right",
              formatter: (r: Row) =>
                r.amountToPay === undefined || r.amountToPay === null
                  ? MISSING
                  : new Amount(CurrencyCode.SEK, r.amountToPay),
            },
          }}
        />
      </Grid>
    </PageLayouts.DashboardLayout>
  );
};

export default VatReportsList;
